import { Link, useParams } from "react-router-dom";
import { BookOpen, User, Tag as TagIcon } from "lucide-react";
import { ArticleCard } from "../../components/ArticleCard";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import { NewsletterBox } from "../../components/NewsletterBox";
import { Seo } from "../../components/Seo";
import { useApi } from "../../hooks/useApi";
import type { Article, Paged } from "../../types";

type Props = {
  type: "category" | "author" | "tag";
};

export function ListingPage({ type }: Props) {
  const { slug } = useParams();
  const { data, loading, error } = useApi<Paged<Article>>(`/api/content/${type}/${slug}`, [type, slug]);
  const formattedTitle = slug ? slug.replace(/-/g, " ") : type;

  if (loading) return <LoadingState label="Curating section stories..." />;
  if (error) return <ErrorState message={error} />;

  const iconMap = {
    category: <BookOpen size={16} className="text-amber-600" />,
    author: <User size={16} className="text-emerald-600" />,
    tag: <TagIcon size={16} className="text-blue-600" />
  };

  const typeLabelMap = {
    category: "EDITORIAL SECTION",
    author: "REPORTER ARCHIVE",
    tag: "SPECIAL TOPIC"
  };

  return (
    <>
      <Seo
        title={`${formattedTitle.toUpperCase()} — ZimSmart Magazine`}
        description={`Read the latest journalism, market analysis, and investigative reporting in ${formattedTitle}.`}
      />

      <div className="mag-container mag-listing-flow">
        {/* Editorial Section Masthead Banner */}
        <header className={`mag-section-banner mag-banner-${slug || type}`}>
          <div className="mag-banner-kicker">
            {iconMap[type]}
            <span>{typeLabelMap[type]}</span>
          </div>

          <h1 className="mag-banner-title">{formattedTitle}</h1>

          <div className="mag-banner-meta">
            <span className="mag-banner-count">
              {data?.items.length || 0} {data?.items.length === 1 ? "Article" : "Articles Published"}
            </span>
            <span className="mag-meta-divider">•</span>
            <span>ZimSmart Digital Edition</span>
          </div>
        </header>

        {/* Stories Grid */}
        {data?.items.length ? (
          <div className="mag-listing-content">
            {/* First Story as Featured if multiple */}
            {data.items.length >= 4 ? (
              <div className="mag-listing-hero">
                <ArticleCard article={data.items[0]} variant="cover" />
                <div className="mag-articles-grid mt-10">
                  {data.items.slice(1).map((article) => (
                    <ArticleCard key={article.id} article={article} variant="compact" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mag-articles-grid">
                {data.items.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="compact" />
                ))}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={`No stories currently filed in ${formattedTitle}`}
            body="New articles published in this section will appear here automatically."
          />
        )}

        <NewsletterBox />
      </div>
    </>
  );
}
